import { Component, OnInit, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { AuthorizationService } from '@nimmsta/ng-commons-lib/auth';
import {
  DateTimePipe,
  NimmstaTimeAgoPipe,
  StatusBadgePipe,
} from '@nimmsta/ng-commons-lib/pipes';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { Job, JobStatus } from '@nimmsta/ng-commons-lib/models';
import { LoadingIndicatorComponent } from '@nimmsta/ng-commons-lib/loading-indicator';
import { JobService } from '../services/job.service';
import { JobWithMdmAndDevice } from '../models/jobWithMdmAndDevice';
import { TitleCasePipe } from '@angular/common';

@Component({
  templateUrl: 'job-detail.component.html',
  styleUrls: ['./job-detail.component.scss'],
  imports: [
    TranslatePipe,
    TitleCasePipe,
    HeaderBarComponent,
    LoadingIndicatorComponent,
    ErrorComponent,
    NimmstaTimeAgoPipe,
    DateTimePipe,
    StatusBadgePipe,
  ],
})
export class JobDetailComponent implements OnInit {
  private authorizationService = inject(AuthorizationService);
  private jobService = inject(JobService);
  private translate = inject(TranslateService);
  private route = inject(ActivatedRoute);

  error?: HttpErrorResponse;

  job?: JobWithMdmAndDevice;

  /** Presigned S3 URL for downloading the job's imported CSV (when job has workflowImport). */
  workflowImportDownloadUrl: string | null = null;

  isLoading = false;

  canWriteJobs = false;

  config: { [key: string]: string[] } = {};

  CONFIG_KEY = 'job-detail-config';

  asString(value: unknown): string {
    return value as string;
  }

  public get JobStatus() {
    return JobStatus;
  }

  ngOnInit(): void {
    this.canWriteJobs = this.authorizationService.hasPermission('job.write');
    this.isLoading = true;
    this.config = JSON.parse(localStorage.getItem(this.CONFIG_KEY) || '{}');
    this.route.params.subscribe((params) => {
      this.jobService.get(params['id']).subscribe({
        next: (job: Job) => {
          this.job = job as JobWithMdmAndDevice;
          this.workflowImportDownloadUrl = null;
          if (this.job.workflowImport?.id) {
            this.jobService
              .getWorkflowImportDownloadUrl(this.job.workflowImport.id)
              .subscribe({
                next: (res) => (this.workflowImportDownloadUrl = res),
                error: () => (this.workflowImportDownloadUrl = null),
              });
          }
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.error = error;
          this.isLoading = false;
        },
      });
    });
  }

  getArticleKeys(article: object): string[] {
    return Object.keys(article);
  }

  getAllArticleKeysForHeaders(articles: object[]): string[] {
    const keys: string[] = [];
    articles.forEach((article) => {
      Object.keys(article).forEach((key) => {
        if (!keys.includes(key)) {
          keys.push(key);
        }
      });
    });
    return keys;
  }

  getResult(result: { [key: string]: unknown }, key: string): string {
    if (typeof result[key] === 'object') {
      return JSON.stringify(result[key]);
    }
    return result[key] as string;
  }

  cancel() {
    this.jobService.cancel(this.job!).subscribe({
      next: () => {
        this.job!.status = JobStatus.CANCELLED;
        this.job!.lastUpdatedAt = dayjs();
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  delete() {
    this.jobService.delete(this.job!).subscribe({
      next: () => {
        this.job!.status = JobStatus.DELETED;
        this.job!.lastUpdatedAt = dayjs();
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  hideColumn(column: string) {
    const dataConnectionId = this.job?.dataConnection?.id;
    if (!dataConnectionId) {
      return;
    }

    if (!this.config[dataConnectionId]) {
      this.config[dataConnectionId] = [];
    }

    if (this.config[dataConnectionId].includes(column)) {
      return;
    }

    this.config[dataConnectionId].push(column);
    this.saveConfig();
  }

  resetColumns() {
    const dataConnectionId = this.job?.dataConnection?.id;
    if (!dataConnectionId) {
      return;
    }

    this.config[dataConnectionId] = [];
    this.saveConfig();
  }

  isHidden(column: string): boolean {
    const dataConnectionId = this.job?.dataConnection?.id;
    if (!dataConnectionId) {
      return false;
    }

    return this.config[dataConnectionId]?.includes(column) ?? false;
  }

  saveConfig() {
    localStorage.setItem(this.CONFIG_KEY, JSON.stringify(this.config));
  }

  isShortPick(result: { [key: string]: unknown }): boolean {
    return (
      (result['scannedArticles'] as number) <
      (result['required_quantity'] as number)
    );
  }

  getWorkflowImportDisplayName(): string {
    const workflowImport = this.job?.workflowImport;
    if (!workflowImport) {
      return '';
    }

    const name = workflowImport.name?.trim();
    if (name) {
      return name;
    }

    const importDate = dayjs(workflowImport.createdAt);
    if (importDate.isValid()) {
      return this.translate.instant('workflowImport.fallbackOnDate', {
        date: importDate.format('YYYY-MM-DD'),
      });
    }

    return this.translate.instant('workflowImport.title');
  }
}
